/** The Stages and queues screen's words. Staff read plain stage names; never a model or a vendor. Pure. */
import type { Formatter } from '@paige/format';
import { reasonPhrase } from '../insights/reason-words';
import type { FlowNote, FlowNoteKind } from './flow-state';
import type { FlowPlace, FlowPool, FlowRun, FlowUnit, FlowWhere } from './flow-types';

export const placeName: Record<FlowPlace, string> = {
  arriving_api: 'Arriving',
  preparing: 'Preparing pages',
  reading: 'Reading',
  splitting: 'Splitting',
  understanding: 'Understanding',
  checking: 'Checking',
  arriving_engine: 'Arriving',
  engine: 'In the engine',
  review: 'Review',
  export: 'Export',
};

const API = 'API batches';
const ENGINE = 'Scan, email, upload';
const BOTH = 'Both roads';

/** The road a place is on, for its detail's heading. */
export const placeRoad: Record<FlowPlace, string> = {
  arriving_api: API,
  preparing: API,
  reading: API,
  splitting: API,
  understanding: API,
  checking: API,
  arriving_engine: ENGINE,
  engine: ENGINE,
  review: BOTH,
  export: BOTH,
};

const UNIT: Record<FlowUnit, [string, string]> = {
  pages: ['page', 'pages'],
  documents: ['document', 'documents'],
  batches: ['batch', 'batches'],
  captures: ['capture', 'captures'],
};

export function unitWord(unit: FlowUnit, n: number): string {
  return UNIT[unit][n === 1 ? 0 : 1];
}

const HOLD: Record<string, string> = {
  no_credits: 'out of pages',
  job_setup: 'job not set up',
  job_off: 'job switched off',
  person: 'for a person',
};
const RETRY: Record<string, string> = {
  retry: 'trying again',
  cortexa_away: 'waiting for Cortexa',
  engine_error: 'the engine is trying again',
};
const PARK: Record<string, string> = {
  capture_parked: 'captures the engine could not read',
  retries_exhausted: 'out of tries',
  unspecified: 'no reason recorded',
};

/** A reason in a phrase: a hold's, a retry's or a park's. */
export function causeWords(kind: FlowNoteKind, reason: string | null): string {
  const code = reason ?? '';
  if (kind === 'hold') return HOLD[code] ?? code.replace(/_/g, ' ');
  if (kind === 'retry') return RETRY[code] ?? 'trying again';
  if (kind === 'slow') return 'waiting longer than this stage should take';
  return PARK[code] ?? reasonPhrase(reason);
}

/** The word on a note's chip in the detail. */
export const noteLabel: Record<FlowNoteKind, string> = {
  ours: 'parked',
  theirs: 'parked',
  slow: 'slow',
  retry: 'retrying',
  hold: 'held',
};

/** Whether a note's way on is the Problems screen (what is parked) rather than the company's page. */
export function leadsToProblems(kind: FlowNoteKind): boolean {
  return kind === 'ours' || kind === 'theirs';
}

const CHIP: Record<FlowNoteKind, (f: Formatter, n: FlowNote) => string> = {
  ours: (f, n) => `${f.int(n.count)} parked, ours to fix`,
  theirs: (f, n) => `${f.int(n.count)} parked, theirs to fix`,
  slow: (f, n) => `Slow, oldest ${f.duration(n.seconds)}`,
  retry: (f, n) => `${f.int(n.count)} ${causeWords('retry', n.reason)}`,
  hold: (f, n) =>
    n.reason === null
      ? `${f.int(n.count)} held`
      : n.reason === 'person'
        ? `${f.int(n.count)} for a person`
        : `${f.int(n.count)} held, ${causeWords('hold', n.reason)}`,
};

/** The few words on a place: "6 parked, ours to fix", "Slow, oldest 14 min", "310 for a person". */
export function chipWords(f: Formatter, n: FlowNote): string {
  // Ours, but nobody needs to act now: acknowledged as known, or put back by Paige itself (Stages and queues' incidents).
  if (n.kind === 'ours' && n.tone === 'held') return `${f.int(n.count)} parked, known`;
  if (n.kind === 'ours' && n.tone === 'slow') return `${f.int(n.count)} parked, Paige retries`;
  return CHIP[n.kind](f, n);
}

const NEXT: Record<Exclude<FlowNoteKind, 'hold'>, string> = {
  ours: 'Needs staff: retry or resolve it on Problems.',
  theirs: 'The company clears this; nothing for staff to do unless they ask.',
  slow: 'Behind, not broken: check the cards below. It reaches Problems only if it fails and parks.',
  retry: 'Paige is trying again by itself.',
};
const NEXT_HOLD: Record<string, string> = {
  person: 'By design: these wait for someone at the company to review them.',
  no_credits: 'By design: read once the company has pages.',
  job_setup: 'By design: read once the job is set up.',
};

/** What to do about a note, in one line. */
export function nextStep(n: FlowNote): string {
  if (n.kind !== 'hold') return NEXT[n.kind];
  return NEXT_HOLD[n.reason ?? ''] ?? 'By design: read once the job is switched on.';
}

/** "Acme, AP invoices" for the oldest item. */
export function whereWords(w: FlowWhere): string {
  return w.jobName ? `${w.companyName}, ${w.jobName}` : w.companyName;
}

const COLUMN: Record<string, FlowPlace> = {
  recognizing: 'reading',
  reading: 'reading',
  organizing: 'splitting',
  understanding: 'understanding',
  finalizing: 'checking',
  complete: 'checking',
};

/** The spine column an engine reading sits under, from the engine's own stage. */
export function runColumn(run: FlowRun): FlowPlace {
  return COLUMN[run.stage ?? ''] ?? 'preparing';
}

/** "Reading", "Setup", "Re-read": what kind of run the engine is on. */
export function runKind(run: FlowRun): string {
  if (run.kind === 'process') return 'Reading';
  if (/setup|bootstrap|analy/i.test(run.kind)) return 'Setup';
  if (/re-?(read|extract)|refresh/i.test(run.kind)) return 'Re-read';
  return 'Working';
}

export const poolName: Record<FlowPool, string> = {
  'page-prep': 'Page preparation',
  ocr: 'Page reader',
  understanding: 'Understanding',
  reasoner: 'Reasoner',
};

/** A card's use in a word a glance takes in. */
export function busyWord(busy: number): 'Full' | 'Busy' | 'Light' | 'Idle' {
  if (busy >= 0.9) return 'Full';
  if (busy >= 0.4) return 'Busy';
  return busy >= 0.03 ? 'Light' : 'Idle';
}
